import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import axios from '../../axios';
import VoucherPrint from './VoucherPrint';
import {
  Container,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Card,
  Autocomplete
} from '@mui/material';
import { toast } from 'react-toastify';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PrintIcon from '@mui/icons-material/Print';

export default function CustomerReceiptForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams(); // Get ID from URL
  const isView = window.location.pathname.includes('/view'); // Check if view mode
  const isEdit = !!id && !isView; // Check if edit mode

  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const branchId = parseInt(localStorage.getItem('branchId'), 10);
  const userId = parseInt(localStorage.getItem('userId'), 10);
  // Security Context
  const restrictedSafeId = localStorage.getItem("safeId") ? parseInt(localStorage.getItem("safeId"), 10) : null;
  const allowedSafeIds = localStorage.getItem("allowedSafeIds") ? JSON.parse(localStorage.getItem("allowedSafeIds")) : [];

  const [form, setForm] = useState({
    companyId,
    branchId,
    voucherNumber: '',
    voucherDate: new Date().toLocaleDateString('en-CA'), // إصلاح التاريخ الافتراضي
    amount: '',
    description: '',
    customerId: '',
    paymentType: 0, // 0 = Cash, 1 = Bank
    safeId: restrictedSafeId || (allowedSafeIds.length === 1 ? allowedSafeIds[0] : '') || '',
    bankId: '',
    userId,
    referenceNumber: '', // إضافة رقم المرجع
    referenceDate: new Date().toLocaleDateString('en-CA'), // إصلاح تاريخ المرجع الافتراضي
    journalEntryGlobalId: null // For linking to JE
  });

  // Lookup data
  const [customers, setCustomers] = useState([]);
  const [safes, setSafes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [nextVoucherNumber, setNextVoucherNumber] = useState('');
  const [companyInfo, setCompanyInfo] = useState(null);
  
  const printRef = useRef();

  const handlePrint = useReactToPrint({
      contentRef: printRef,
      documentTitle: `Voucher-${form.voucherNumber || 'New'}`,
  });

  // Prepare data for print
  const voucherForPrint = {
      ...form,
      voucherType: 101, // Customer Receipt
      customerName: customers.find(c => c.id === form.customerId)?.name || '',
      safeName: safes.find(s => s.safeId === form.safeId)?.name || '',
      bankName: banks.find(b => b.id === form.bankId)?.name || '',
      voucherNumber: id ? form.voucherNumber : nextVoucherNumber
  };

  // Load next voucher number 
  useEffect(() => { 
  if (!companyId || !branchId || id) return; // Don't fetch if editing/viewing
  
  const loadNextVoucherNumber = async () => { 
    try { 
      const response = await axios.get(`/Vouchers/next-number/${companyId}/${branchId}/101`); // تغيير من 0 إلى 1
      setNextVoucherNumber(response.data.nextNumber || response.data.toString()); 
    } catch (err) { 
      console.error('Error loading next voucher number:', err); 
      setNextVoucherNumber('1'); 
    } 
  }; 

  loadNextVoucherNumber(); 
}, [companyId, branchId, id]); // إزالة القيمة 0 من dependency array

  // Load lookup data
  useEffect(() => {
    if (!companyId) return;
    
    const loadData = async () => {
      setLoading(true);
      try {
        const [customersRes, safesRes, banksRes, companyRes] = await Promise.all([
          axios.get(`/Customer/by-company/${companyId}`),
          axios.get(`/Safe/by-company/${companyId}`),
          axios.get(`/Bank/by-company/${companyId}`),
          axios.get(`/CompanyProfile/public/${companyId}`)
        ]);
        
        setCustomers(customersRes.data || []);
        setSafes(safesRes.data || []);
        setBanks(banksRes.data || []);
        setCompanyInfo(companyRes.data);

        // Load Voucher Data if ID exists
        if (id) {
             const res = await axios.get(`/Vouchers/${id}`);
             const v = res.data;
             setForm(prev => ({
                 ...prev,
                 voucherNumber: v.voucherNumber,
                 voucherDate: v.voucherDate.split('T')[0],
                 amount: v.amount,
                 description: v.description,
                 customerId: v.customerId,
                 paymentType: v.paymentType === 1 ? 0 : 1, // API: 1=Cash, 2=Bank. Form: 0=Cash, 1=Bank
                 safeId: v.safeId || '',
                 bankId: v.bankId || '',
                 referenceNumber: v.referenceNumber || '',
                 referenceDate: v.referenceDate ? v.referenceDate.split('T')[0] : '',
                 journalEntryGlobalId: v.journalEntryGlobalId
             }));
             setNextVoucherNumber(v.voucherNumber);
        }

      } catch (err) {
        console.error('Error loading data:', err);
        setError('فشل في تحميل البيانات');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [companyId, id]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isView) return;

    // Validation
    if (!form.customerId) {
      setError('يرجى اختيار العميل');
      return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setError('يرجى إدخال مبلغ صحيح');
      return;
    }
    if (form.paymentType === 0 && !form.safeId) {
      setError('يرجى اختيار الخزنة للدفع النقدي');
      return;
    }
    if (form.paymentType === 1 && !form.bankId) {
      setError('يرجى اختيار البنك للدفع البنكي');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const voucherData = {
        id: id ? parseInt(id) : 0,
        companyId: form.companyId,
        branchId: form.branchId,
        voucherType: 101, // CustomerReceipt = 1 (not 0)
        paymentType: form.paymentType === 0 ? 1 : 2, // Cash = 1, Bank = 2
        customerId: parseInt(form.customerId),
        amount: parseFloat(form.amount),
        voucherDate: form.voucherDate + 'T12:00:00.000Z', // إضافة وقت الظهر لتجنب مشاكل المنطقة الزمنية
        description: form.description || '',
        notes: form.description || '',
        safeId: form.paymentType === 0 ? parseInt(form.safeId) : null,
        bankId: form.paymentType === 1 ? parseInt(form.bankId) : null,
        userId: form.userId,
        createdBy: localStorage.getItem('userName') || 'System',
        referenceNumber: form.referenceNumber || null,
        referenceDate: form.referenceDate ? form.referenceDate + 'T12:00:00.000Z' : null // إضافة وقت الظهر
      };
    
      if (id) {
          // Update
          await axios.put(`/Vouchers/${id}`, voucherData); // Assuming PUT endpoint exists
          toast.success('تم تحديث سند القبض بنجاح');
      } else {
          // Create
          await axios.post('/Vouchers/customer-receipt', voucherData);
          toast.success('تم إنشاء سند القبض بنجاح');
      }
      
      navigate('/vouchers-list');
    } catch (err) {
      console.error('Error creating/updating voucher:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء حفظ السند');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }} dir="rtl">
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 3, display: 'flex', alignItems: 'center' }}>
          <ReceiptIcon sx={{ fontSize: 40, mr: 2, color: 'white' }} />
          <Typography variant="h4" fontWeight="bold" color="inherit">
            سند قبض من عميل
          </Typography>
          <Box flexGrow={1} />
          <Chip 
            label={`# ${id ? form.voucherNumber : nextVoucherNumber}`} 
            sx={{ 
              bgcolor: 'white', 
              color: 'primary.main', 
              fontSize: '1.2rem', 
              fontWeight: 'bold',
              px: 2
            }} 
          />
        </Box>

        {/* Hidden Print Component */}
        <div style={{ display: 'none' }}>
            <VoucherPrint 
                ref={printRef} 
                voucher={voucherForPrint}
                companyInfo={companyInfo}
            />
        </div>

        <Box p={4}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Top Section: Basic Info */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="رقم السند"
                  value={id ? form.voucherNumber : nextVoucherNumber}
                  disabled
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="تاريخ السند"
                  type="date"
                  name="voucherDate"
                  value={form.voucherDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="رقم المرجع"
                  name="referenceNumber"
                  value={form.referenceNumber}
                  onChange={handleChange}
                  disabled={isView}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="تاريخ المرجع"
                  type="date"
                  name="referenceDate"
                  value={form.referenceDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={isView}
                />
              </Grid>
            </Grid>

            {/* Middle Section: Financials and Payment */}
            <Card variant="outlined" sx={{ p: 3, mb: 4, bgcolor: '#fff', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom color="primary.main" fontWeight="bold">
                💰 بيانات العميل والدفع
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                {/* Row 1: Customer, Payment Type, Safe/Bank */}
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => option.name || ''}
                    value={customers.find(c => c.id === form.customerId) || null}
                    onChange={(e, newValue) => {
                      setForm(prev => ({ ...prev, customerId: newValue ? newValue.id : '' }));
                    }}
                    disabled={isView}
                    renderInput={(params) => <TextField {...params} label="العميل" required />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>نوع الدفع</InputLabel>
                    <Select
                      name="paymentType"
                      value={form.paymentType}
                      onChange={handleChange}
                      label="نوع الدفع"
                      disabled={isView}
                    >
                      <MenuItem value={0}>💵 نقدي</MenuItem>
                      <MenuItem value={1}>🏦 بنكي</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  {form.paymentType === 0 ? (
                    <Autocomplete
                      options={safes.filter(s => {
                        if (restrictedSafeId) return s.safeId === restrictedSafeId;
                        if (allowedSafeIds.length > 0) return allowedSafeIds.includes(s.safeId);
                        return true;
                      })}
                      getOptionLabel={(option) => option.name || ''}
                      value={safes.find(s => s.safeId === form.safeId) || null}
                      onChange={(e, newValue) => {
                        setForm(prev => ({ ...prev, safeId: newValue ? newValue.safeId : '' }));
                      }}
                      disabled={!!restrictedSafeId || isView}
                      renderInput={(params) => <TextField {...params} label="الصندوق" required />}
                    />
                  ) : (
                    <Autocomplete
                      options={banks}
                      getOptionLabel={(option) => option.name || ''}
                      value={banks.find(b => b.id === form.bankId) || null}
                      onChange={(e, newValue) => {
                        setForm(prev => ({ ...prev, bankId: newValue ? newValue.id : '' }));
                      }}
                      disabled={isView}
                      renderInput={(params) => <TextField {...params} label="البنك" required />}
                    />
                  )}
                </Grid>

                {/* Row 2: Reference Info */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="رقم المرجع"
                    name="referenceNumber"
                    value={form.referenceNumber}
                    onChange={handleChange}
                    disabled={isView}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="تاريخ المرجع"
                    type="date"
                    name="referenceDate"
                    value={form.referenceDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={isView}
                  />
                </Grid>

                {/* Divider */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                </Grid>

                {/* Row 3: Amount */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="المبلغ"
                    name="amount"
                    type="number"
                    value={form.amount}
                    onChange={handleChange}
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    disabled={isView}
                    sx={{ 
                      '& input': { fontSize: '1.2rem', fontWeight: 'bold', color: '#2e7d32' }
                    }}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* Bottom Section: Description */}
            <Box mt={4}>
              <Typography variant="h6" gutterBottom color="text.secondary" fontWeight="bold">
                📝 البيان / الملاحظات
              </Typography>
              <TextField
                fullWidth
                name="description"
                value={form.description}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="اكتب وصفاً تفصيلياً للسند..."
                disabled={isView}
                sx={{ 
                  bgcolor: '#f5f5f5',
                  '& .MuiOutlinedInput-root': { fontSize: '1.1rem' } 
                }}
              />
            </Box>

            {/* Actions */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={4} pt={2} borderTop={1} borderColor="divider">
              {form.journalEntryGlobalId && (
                <Button 
                  variant="outlined" 
                  color="secondary"
                  size="large"
                  onClick={() => window.open(`/journal-entries/${form.journalEntryGlobalId}/view`, "_blank")}
                >
                  👁️ عرض القيد المحاسبي
                </Button>
              )}
              <Button
                  variant="outlined"
                  color="info"
                  size="large"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
              >
                  طباعة
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                onClick={() => navigate('/vouchers-list')}
                disabled={submitting}
              >
                {isView ? 'رجوع' : 'إلغاء'}
              </Button>
              {!isView && (
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <ReceiptIcon />}
                  sx={{ px: 4, py: 1, fontSize: '1.1rem' }}
                >
                  {submitting ? 'جاري الحفظ...' : (id ? 'تحديث السند' : 'حفظ السند')}
                </Button>
              )}
            </Box>
          </form>
        </Box>
      </Paper>
    </Container>
  );
}